/**
 * Per-gauge assignment settings (stored as JSON in gauge_assignments.settings) and
 * organization defaults (organizations.settings). One schema, used by API, worker and panel.
 */
import { z } from 'zod';

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'colour must be #RRGGBB');
const gaugeRef = z.string().regex(/^[a-z0-9][a-z0-9_-]*$/);

/** Green/amber/red status. `amber_at` / `red_at` are KPI values. */
export const statusRuleSchema = z
  .strictObject({
    direction: z.enum(['higher_is_better', 'lower_is_better']),
    amber_at: z.number(),
    red_at: z.number(),
  })
  .refine((r) => (r.direction === 'higher_is_better' ? r.red_at <= r.amber_at : r.red_at >= r.amber_at), {
    message: 'red_at must be worse than amber_at for the chosen direction',
  });

/** Alert: lights a warning lamp (and optionally pins the needle into the redline) when crossed. */
export const alertRuleSchema = z.strictObject({
  when: z.enum(['above', 'below']),
  threshold: z.number(),
  /** Warning light (gauge id of type warning_light) to switch on. */
  light: gaugeRef.optional(),
  /** Blink that light instead of lighting it steadily. */
  light_blink: z.boolean().default(false),
  /** 'value' keeps showing the real value; 'redline' pushes the needle to the redline start. */
  needle: z.enum(['value', 'redline']).default('value'),
});

export const staleOverrideSchema = z.strictObject({
  /** Warning light to switch on while this gauge's value is stale. */
  light: gaugeRef.optional(),
});

export const needleSettingsSchema = z
  .strictObject({
    kind: z.literal('needle'),
    unit_label: z.string().max(24).default(''),
    /** KPI range mapped linearly onto the dial's physical range. kpi_min > kpi_max is allowed (inverted). */
    kpi_min: z.number(),
    kpi_max: z.number(),
    /** Printed-scale multiplier: dial label = KPI / multiplier, shown as "×1000". Display only. */
    multiplier: z.number().positive().default(1),
    /** Where the redline starts, in KPI units. */
    redline_start: z.number().optional(),
    status: statusRuleSchema.optional(),
    alert: alertRuleSchema.optional(),
    stale: staleOverrideSchema.optional(),
    /** Needle smoothing: approximate settle time in ms (98% of the way), 0 = instant. */
    damping_ms: z.number().int().min(0).max(10_000).default(800),
  })
  .refine((s) => s.kpi_min !== s.kpi_max, { message: 'kpi_min and kpi_max must differ', path: ['kpi_max'] });

export const segmentSettingsSchema = z.strictObject({
  kind: z.literal('segment'),
  decimals: z.number().int().min(0).max(4).default(0),
  /** Shorten large numbers: 12500 → "12.5K". */
  compact: z.boolean().default(true),
  suffix: z.string().max(4).default(''),
  status: statusRuleSchema.optional(),
  alert: alertRuleSchema.optional(),
  stale: staleOverrideSchema.optional(),
});

/**
 * A warning light assigned directly.
 * - 'rule': on while the KPI is above/below `threshold`; blinks when `blink` is set, or once the KPI
 *   also crosses `blink_at` (same direction, further out): steady first, blinking when it gets worse.
 * - 'manual': a fixed state chosen in the panel (no KPI), e.g. to test a lamp or flag an announcement.
 */
export const lightSettingsSchema = z
  .strictObject({
    kind: z.literal('light'),
    mode: z.enum(['rule', 'manual']).default('rule'),
    when: z.enum(['above', 'below']).default('above'),
    threshold: z.number().default(0),
    blink: z.boolean().default(false),
    blink_at: z.number().optional(),
    manual: z.enum(['off', 'on', 'blink']).default('off'),
  })
  .refine((s) => s.blink_at === undefined || (s.when === 'above' ? s.blink_at > s.threshold : s.blink_at < s.threshold), {
    message: 'blink_at must be further than threshold in the alert direction',
    path: ['blink_at'],
  });

export const assignmentSettingsSchema = z.discriminatedUnion('kind', [needleSettingsSchema, segmentSettingsSchema, lightSettingsSchema]);

export type StatusRule = z.infer<typeof statusRuleSchema>;
export type AlertRule = z.infer<typeof alertRuleSchema>;
export type NeedleSettings = z.infer<typeof needleSettingsSchema>;
export type SegmentSettings = z.infer<typeof segmentSettingsSchema>;
export type LightSettings = z.infer<typeof lightSettingsSchema>;
export type AssignmentSettings = z.infer<typeof assignmentSettingsSchema>;

export const DEFAULT_COLORS = {
  ok: '#1FAA59',
  warn: '#F2A93B',
  alert: '#E3242B',
  stale: '#3D7BFF',
  neutral: '#FFFFFF',
} as const;

export const orgSettingsSchema = z.strictObject({
  stale: z
    .strictObject({
      /** Default when a metric has no own stale_after_minutes. */
      after_minutes: z.number().int().min(1).max(10_080).default(60),
      /** What a needle does while stale: keep last value, or rest at the dial minimum. */
      needle: z.enum(['hold', 'min']).default('hold'),
    })
    .default({ after_minutes: 60, needle: 'hold' }),
  colors: z
    .strictObject({ ok: hex, warn: hex, alert: hex, stale: hex, neutral: hex })
    .default({ ...DEFAULT_COLORS }),
});
export type OrgSettings = z.infer<typeof orgSettingsSchema>;
export const defaultOrgSettings = (): OrgSettings => orgSettingsSchema.parse({});
